import { defineComponent as _defineComponent } from 'vue'
import { waitUntil } from 'async-wait-until';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { ref, computed, Ref, watch, ComputedRef } from 'vue';
import { Component } from 'vue/types/umd';
// import { applyReactInVue } from 'vuereact-combined';

import { Modes } from '@/common.js';
import { useBilling } from '@/components/Billing/Billing.composables';
// @ts-ignore
import BillingAutomationSlider from '@/components/Billing/BillingAutomationSlider.vue';
// @ts-ignore
import BillingCustomerFeatures from '@/components/Billing/BillingCustomerFeatures.vue';
// @ts-ignore
import BillingPageHeader from '@/components/Billing/BillingPageHeader.vue';
// @ts-ignore
import BillingPlyCreditsSlider from '@/components/Billing/BillingPlyCreditsSlider.vue';
// @ts-ignore
import BillingSummaryBar from '@/components/Billing/BillingSummaryBar.vue';
// @ts-ignore
import BillingTeamFeatures from '@/components/Billing/BillingTeamFeatures.vue';
// @ts-ignore
import BillingService from '@/components/Billing/billing-service';
import { COMPARE_BILLING_PLANS_URL, BILLING_FAQ_URL } from '@/components/Billing/billing-service.const';
import { FeatureTypes, BillingPeriod, Product, ProductType, PurchaseDetails, Subscription } from '@/components/Billing/billing-types';
// import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
// import SmartUpgradeConfiguration from '@/react_app/components/BillingUsage/Settings/SmartUpgradeConfiguration';
import IntercomService from '@/utils/IntercomService';
import { isNullOrUndefined } from '@/utils/helpers';
// const SmartUpgradeConfigurationVue = applyReactInVue(SmartUpgradeConfiguration);
// @ts-ignore
import InAppInjectables from '@/views/common/InAppInjectables';

interface Section {
	props?: object;
	class?: string;
	current?: string;
	selected?: string;
	listeners?: object;
	show: () => boolean;
	productType?: ProductType;
	initializeValue?: () => void;
	component: string | Component;
	filterBy?: (plan: Product) => boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BillingMainPage',
  setup(__props) {

const {
	billingPeriod,
	subscriptions,
	creditsSubscription,
	// isSmartUpgradeEnabled,
	automationSubscription,
	teamFeaturesSubscription,
	customerFeatureSubscription,
	setDocsOpen,
	usePlanById,
	getProducts,
	getCustomerData,
	getEntitlements,
	getPaymentMethod,
	useEntitlementById,
	getAccountSubscriptions,
} = useBilling();
const planById = usePlanById();
const entitlementById = useEntitlementById();
const isLoading = ref(false);
const reRenderCards = ref(0);
const didSomethingGoWrong = ref(false);

const EXTRAS_KEY = 'extras';

const tabs = [
	{
		title: 'Team features',
		key: ProductType.TeamFeatures,
		icon: 'billing-team-features-section',
	},
	{
		title: 'Customer features',
		key: ProductType.CustomerFeatures,
		icon: 'billing-customer-features-section',
	},
	{
		title: 'Extras',
		key: EXTRAS_KEY,
		icon: 'billing-extras',
	},
];
const selectedTab = ref(tabs[0].key);

const initializeData = async () => {
	isLoading.value = true;
	didSomethingGoWrong.value = false;
	try {
		await Promise.all([getProducts(), getPaymentMethod(), getAccountSubscriptions(), getCustomerData(), getEntitlements({})]);
	} catch (e) {
		didSomethingGoWrong.value = true;
		console.error('Billing main page - error getting the plans', e);
	} finally {
		isLoading.value = false;
	}
};
const isSectionToSelectPlan = (section: Section) => !!section.productType;
const onLoad = () => {
	sectionsDetail.value.forEach((section) => {
		if (isSectionToSelectPlan(section)) {
			if (!section.productType) {
				throw new Error('section.productType is required');
			}
			const relevantSubscription = BillingService.getSubscriptionByProductType(subscriptions.value, section.productType);
			if (!relevantSubscription) {
				throw new Error('relevantSubscription is required');
			}
			if (section.initializeValue) {
				section.initializeValue();
			} else {
				section.current = BillingService.getRelevantPlanIdForSubscription(relevantSubscription);
			}
			section.selected = section.current;
		}
	});
	isCurrentTeamTasksVirtuallyUnlimited.value = BillingService.isSubscriptionVirtuallyUnlimited(teamFeaturesSubscription.value);
	const customerFeatureEntitlement = entitlementById.value(FeatureTypes.CustomerTasks);
	if (customerFeatureEntitlement) {
		currentAccountCustomerFeatureQuantity.value = BillingService.getCustomerFeatureUsageLimit(
			customerFeatureSubscription.value,
			false,
			customerFeatureEntitlement
		);
	} else {
		currentAccountCustomerFeatureQuantity.value = 0;
	}

	selectedCustomerFeatureQuantity.value = currentAccountCustomerFeatureQuantity.value;
};
const newPurchaseDetails = computed(() => ({
	billingPeriod: billingPeriod.value,
	teamFeature: { id: selectedTeamFeatureProduct.value },
	credits: { quantity: selectedPlyCreditsUsageLimitAmount.value },
	automationTasks: { quantity: selectedAutomationUsageLimitAmount.value },
	customerFeature: { id: selectedCustomerFeatureProduct.value, quantity: selectedCustomerFeatureQuantity.value },
})) as ComputedRef<PurchaseDetails>;
const currentPurchaseDetails = computed(() => ({
	billingPeriod: currentAccountBillingPeriod.value,
	teamFeature: { id: currentAccountTeamFeatureProduct.value },
	credits: { quantity: currentAccountPlyCreditsUsageLimitAmount.value },
	automationTasks: { quantity: currentAccountAutomationUsageLimitAmount.value },
	customerFeature: { id: currentAccountCustomerFeatureProduct.value, quantity: currentAccountCustomerFeatureQuantity.value },
})) as ComputedRef<PurchaseDetails>;

const scrollToTeamFeatures = () => (selectedTab.value = ProductType.TeamFeatures);
const scrollToCustomerFeatures = () => (selectedTab.value = ProductType.CustomerFeatures);
const scrollToAutomation = () => (selectedTab.value = EXTRAS_KEY);
const scrollToPlyCredits = () => (selectedTab.value = EXTRAS_KEY);
const scrollToFAQ = () => setDocsOpen(BILLING_FAQ_URL);
const currentAccountBillingPeriod = computed(() =>
	BillingService.isAnnuallySubscription(subscriptions.value) ? BillingPeriod.annually : BillingPeriod.monthly
);

const isCurrentTeamTasksVirtuallyUnlimited = ref(false);
const selectedTeamFeatureProduct = ref() as Ref<string>;
const currentAccountTeamFeatureProduct = ref() as Ref<string>;

const selectedCustomerFeatureProduct = ref() as Ref<string>;
const currentAccountCustomerFeatureProduct = ref() as Ref<string>;
const currentAccountCustomerFeatureQuantity = ref(0) as Ref<number>;
const selectedCustomerFeatureQuantity = ref(currentAccountCustomerFeatureQuantity.value);

const selectedAutomationUsageLimitAmount = ref() as Ref<number>;
const currentAccountAutomationUsageLimitAmount = ref() as Ref<number>;

const selectedPlyCreditsUsageLimitAmount = ref() as Ref<number>;
const currentAccountPlyCreditsUsageLimitAmount = ref() as Ref<number>;

const didFinishLoadingData = computed(
	() =>
		selectedTeamFeatureProduct.value &&
		selectedCustomerFeatureProduct.value &&
		!isNullOrUndefined(currentAccountAutomationUsageLimitAmount.value) &&
		!isNullOrUndefined(currentAccountPlyCreditsUsageLimitAmount.value)
);
const sectionsDetail = ref([
	// {
	// 	component: SmartUpgradeConfigurationVue,
	//  show:  !isSmartUpgradeEnabled.value,
	// 	props: { isSmartUpgradeOn: isSmartUpgradeEnabled.value, onToggleSmartUpgradeState: AppDialogManagerService.openSmartUpgradeDialog },
	// },
	{
		component: BillingTeamFeatures,
		selected: selectedTeamFeatureProduct,
		productType: ProductType.TeamFeatures,
		current: currentAccountTeamFeatureProduct,
		show: () => selectedTab.value === ProductType.TeamFeatures,
		props: { selectedTeamFeatureProduct, currentAccountTeamFeatureProduct, isCurrentTeamTasksVirtuallyUnlimited },
		listeners: {
			'select-plan': (planId: string) => (selectedTeamFeatureProduct.value = planId),
		},
	},
	{
		component: BillingCustomerFeatures,
		selected: selectedCustomerFeatureProduct,
		productType: ProductType.CustomerFeatures,
		current: currentAccountCustomerFeatureProduct,
		show: () => selectedTab.value === ProductType.CustomerFeatures,
		props: { selectedCustomerFeatureProduct, currentAccountCustomerFeatureProduct, currentAccountCustomerFeatureQuantity },
		listeners: {
			'select-plan': (planId: string) => (selectedCustomerFeatureProduct.value = planId),
			'update-customer-features-tasks-quantity': (customerFeaturesTasksQuantity: number) =>
				(selectedCustomerFeatureQuantity.value = customerFeaturesTasksQuantity),
		},
	},
	{
		productType: ProductType.Automation,
		component: BillingAutomationSlider,
		selected: selectedAutomationUsageLimitAmount,
		current: currentAccountAutomationUsageLimitAmount,
		props: { currentQuantity: currentAccountAutomationUsageLimitAmount },
		listeners: { 'update-amount': (usageLimit: number) => (selectedAutomationUsageLimitAmount.value = usageLimit) },
		show: () => selectedTab.value === EXTRAS_KEY,
		initializeValue: () => {
			currentAccountAutomationUsageLimitAmount.value = getInitialSliderValue(FeatureTypes.AutomationTasks, automationSubscription.value);
		},
	},
	{
		class: 'mt-10',
		productType: ProductType.Credits,
		component: BillingPlyCreditsSlider,
		selected: selectedPlyCreditsUsageLimitAmount,
		current: currentAccountPlyCreditsUsageLimitAmount,
		props: { currentQuantity: currentAccountPlyCreditsUsageLimitAmount },
		listeners: { 'update-amount': (usageLimit: number) => (selectedPlyCreditsUsageLimitAmount.value = usageLimit) },
		show: () => selectedTab.value === EXTRAS_KEY,
		initializeValue: () => {
			currentAccountPlyCreditsUsageLimitAmount.value = getInitialSliderValue(FeatureTypes.Credits, creditsSubscription.value);
		},
	},
]) as Ref<Section[]>;

const getInitialSliderValue = (featureType: FeatureTypes, subscription: Subscription) => {
	const entitlement = entitlementById.value(featureType);
	if (!subscription || !entitlement) {
		return 0;
	} else {
		return BillingService.getSubscriptionSliderValue(subscription, planById.value, false, entitlement);
	}
};

const waitForIntercom = async () => {
	if (process.env.NODE_ENV === Modes.Production) {
		await waitUntil(() => IntercomService.loaded() && IntercomService.booted(), { timeout: 10000 });
		IntercomService.update({ alignment: 'right' }, false);
	}
};
waitForIntercom();
initializeData().then(onLoad);
watch(subscriptions, (newValue, oldValue) => {
	if (!isEmpty(newValue) && !isEmpty(oldValue) && !isEqual(newValue, oldValue)) {
		onLoad();
		reRenderCards.value += 1;
	}
});
const scrollToRelevantSection = () => {
	const hash = window.location.hash;
	if (hash === `#${ProductType.TeamFeatures}`) {
		scrollToTeamFeatures();
	} else if (hash === `#${ProductType.CustomerFeatures}`) {
		scrollToCustomerFeatures();
	} else if (hash === `#${ProductType.Automation}`) {
		scrollToAutomation();
	} else if (hash === `#${ProductType.Credits}`) {
		scrollToPlyCredits();
	} else if (hash === `#faq`) {
		scrollToFAQ();
	}
};
scrollToRelevantSection();

return { __sfc: true,billingPeriod, subscriptions, creditsSubscription, automationSubscription, teamFeaturesSubscription, customerFeatureSubscription, setDocsOpen, usePlanById, getProducts, getCustomerData, getEntitlements, getPaymentMethod, useEntitlementById, getAccountSubscriptions, planById, entitlementById, isLoading, reRenderCards, didSomethingGoWrong, EXTRAS_KEY, tabs, selectedTab, initializeData, isSectionToSelectPlan, onLoad, newPurchaseDetails, currentPurchaseDetails, scrollToTeamFeatures, scrollToCustomerFeatures, scrollToAutomation, scrollToPlyCredits, scrollToFAQ, currentAccountBillingPeriod, isCurrentTeamTasksVirtuallyUnlimited, selectedTeamFeatureProduct, currentAccountTeamFeatureProduct, selectedCustomerFeatureProduct, currentAccountCustomerFeatureProduct, currentAccountCustomerFeatureQuantity, selectedCustomerFeatureQuantity, selectedAutomationUsageLimitAmount, currentAccountAutomationUsageLimitAmount, selectedPlyCreditsUsageLimitAmount, currentAccountPlyCreditsUsageLimitAmount, didFinishLoadingData, sectionsDetail, getInitialSliderValue, waitForIntercom, scrollToRelevantSection, BillingPageHeader, BillingSummaryBar, COMPARE_BILLING_PLANS_URL, InAppInjectables }
}

})